/* ═══════════════════════════════════════════════════════════════════
   PROVENANCE — the "Provenance Fragment" solo museum game.

   All logic runs CLIENT-SIDE (zero cost).  Progress is persisted to
   localStorage so the user can pick up where they left off.  The backend
   is only touched when the user explicitly completes the game (POST
   /api/provenance/progress) so the leaderboard / achievements can be
   stored — but never blocks gameplay.

   Ships with:
     • Provenance Fragment  — restore a fractured memory (5×5 swap)
     • EchoesOfEmpire       — kept as an export for TheEcho tile only;
                              not shown in the Provenance launcher itself.

   Archived (future use, restore from `_backups/Provenance.full.jsx.bak`):
     • Constellation Harmony
     • Legacy Duel
     • Empire's Harmony
   ═══════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BackPill } from './BackPill';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/api';
import { EmojiArt } from '../EmojiArt';
import * as EchoSFX from '../../utils/echoSounds';
import * as HarmonySFX from '../../utils/harmonySounds';
import { vibrate as nativeVibrate } from '../../utils/haptics';

const GOLD = '#D4AF37';
const GOLD_HI = '#FFE9A0';

const STORAGE_KEY = 'provenance_progress_v1';
const loadProgress = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (_) { return {}; }
};
const saveProgress = (next) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
};

/* ───────── Shared Imperial backdrop (ambient stars + rotating gold ring) ─────────
   Used as a non-interactive background under every game.  Pure CSS so it costs
   nothing on the GPU even on low-end phones. */
const STAR_FIELD = Array.from({ length: 36 }, (_, i) => ({
  left: (i * 137.5) % 100,                 // golden-angle distribution
  top:  ((i * 91.7) + (i % 7) * 13) % 100,
  size: 1 + (i % 3) * 0.6,
  delay: (i % 12) * 0.4,
  dur: 3 + (i % 5) * 0.8,
}));

const ImperialBackdrop = React.memo(({ intensity = 0.6 }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden style={{ opacity: intensity }}>
    {/* twinkling stars */}
    {STAR_FIELD.map((s, i) => (
      <span key={i} className="absolute rounded-full" style={{
        left: `${s.left}%`, top: `${s.top}%`,
        width: s.size, height: s.size,
        background: i % 4 === 0 ? GOLD_HI : '#FFFFFF',
        boxShadow: `0 0 ${s.size * 4}px ${i % 4 === 0 ? GOLD : '#FFFFFF'}`,
        animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
      }}/>
    ))}
    {/* slowly-rotating gold filigree ring */}
    <svg className="absolute" style={{
      width: '140vmin', height: '140vmin',
      left: '50%', top: '50%',
      transform: 'translate(-50%, -50%)',
      opacity: 0.06,
      animation: 'imperialSpin 180s linear infinite',
    }} viewBox="0 0 200 200">
      <defs>
        <radialGradient id="imperialRingG" cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="transparent"/>
          <stop offset="90%" stopColor={GOLD}/>
          <stop offset="100%" stopColor="transparent"/>
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r="80" fill="none" stroke={GOLD} strokeWidth="0.4" strokeDasharray="1 3"/>
      <circle cx="100" cy="100" r="70" fill="none" stroke={GOLD_HI} strokeWidth="0.25"/>
      <circle cx="100" cy="100" r="60" fill="none" stroke={GOLD} strokeWidth="0.3" strokeDasharray="4 2"/>
      {[...Array(12)].map((_, i) => {
        const a = (i / 12) * Math.PI * 2;
        return <circle key={i} cx={100 + Math.cos(a) * 75} cy={100 + Math.sin(a) * 75} r="1.2" fill={GOLD}/>;
      })}
    </svg>
    <style>{`
      @keyframes twinkle      { 0%,100%{opacity:0.15;transform:scale(0.7)} 50%{opacity:1;transform:scale(1.2)} }
      @keyframes imperialSpin { from{transform:translate(-50%,-50%) rotate(0deg)} to{transform:translate(-50%,-50%) rotate(360deg)} }
    `}</style>
  </div>
));


/* ───────────────────────── Launcher Tile Icon ───────────────────────── */
export const ProvenanceTileIcon = ({ size = 28 }) => (
  /* ── PROVENANCE — four fragmented squares reassembling into a whole,
        signalling the 5×5 swap puzzle that restores a shattered memory. */
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none"
    style={{ filter: 'drop-shadow(0 0 8px rgba(216,121,121,0.55))' }}>
    <defs>
      <linearGradient id="provGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFE9A0"/><stop offset="1" stopColor="#8B6B1F"/>
      </linearGradient>
    </defs>
    {/* Top-left fragment (aligned) */}
    <rect x="4" y="4" width="9" height="9" rx="1"
      fill="url(#provGrad)" fillOpacity="0.28"
      stroke="url(#provGrad)" strokeWidth="1.2"/>
    {/* Top-right fragment — slightly tilted (out of place) */}
    <g transform="rotate(-8 19.5 8.5)">
      <rect x="15" y="4" width="9" height="9" rx="1"
        fill="url(#provGrad)" fillOpacity="0.28"
        stroke="url(#provGrad)" strokeWidth="1.2"/>
    </g>
    {/* Bottom-left fragment — slightly tilted (out of place) */}
    <g transform="rotate(6 8.5 19.5)">
      <rect x="4" y="15" width="9" height="9" rx="1"
        fill="url(#provGrad)" fillOpacity="0.28"
        stroke="url(#provGrad)" strokeWidth="1.2"/>
    </g>
    {/* Bottom-right fragment (aligned) */}
    <rect x="15" y="15" width="9" height="9" rx="1"
      fill="url(#provGrad)" fillOpacity="0.28"
      stroke="url(#provGrad)" strokeWidth="1.2"/>
    {/* Centre reassembly spark */}
    <circle cx="14" cy="14" r="1.2" fill="#FFF4C4"/>
  </svg>
);

/* ───────────────────────── Game 2: Provenance Fragment (5×5 swap puzzle) ─────────────────────────

   Zero-cost technique: a single <img> is loaded once and 25 absolutely-positioned
   <button>s each show a different fragment via background-position. The buttons
   move using transform: translate() so the GPU handles all animation. Solved
   state triggers a single CSS keyframe that fades the sepia away on every tile
   simultaneously and pulses a gold flare.
   ─────────────────────────────────────────────────────────────────────────── */

const GRID_SIZE = 5;
const TILE_COUNT = GRID_SIZE * GRID_SIZE;

const fisherYates = (n) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Avoid the (extremely unlikely) case where shuffle returns the solved order.
const shuffleUntilUnsolved = () => {
  let order = fisherYates(TILE_COUNT);
  while (order.every((v, i) => v === i)) order = fisherYates(TILE_COUNT);
  return order;
};

const ProvenanceFragment = ({ onBack, memories }) => {
  // Pool = user's image memories, LATEST FIRST. Cursor loops back to 0 at end.
  const imagePool = useMemo(() => {
    const list = (memories || []).filter(
      (m) => m.image_url && m.media_type !== 'video' && m.media_type !== 'audio'
    );
    // Sort newest → oldest (fallback to id if created_at missing).
    return list.slice().sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return String(b.id).localeCompare(String(a.id));
    });
  }, [memories]);

  const [poolIdx, setPoolIdx] = useState(0);
  const imageUrl = imagePool.length > 0 ? imagePool[poolIdx % imagePool.length].image_url : null;

  // tiles[correctPos] = currentPos — the position this tile currently occupies on the board.
  const [tiles, setTiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // correctPos of selected tile
  const [solved, setSolved] = useState(false);
  const [moves, setMoves] = useState(0);
  // PREMIUM: timer + star rating
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);       // ms — live during play
  const [finalElapsed, setFinalElapsed] = useState(0);  // ms — frozen on solve
  const postedRef = useRef(false);

  // Auto-start / re-deal whenever the current image slot changes.
  useEffect(() => {
    if (!imageUrl) return;
    setTiles(shuffleUntilUnsolved());
    setSelectedId(null);
    setSolved(false);
    setMoves(0);
    setStartedAt(Date.now());
    setElapsed(0);
    setFinalElapsed(0);
    postedRef.current = false;
  }, [imageUrl, poolIdx]);

  // Live timer (RAF-cheap: 2Hz interval)
  useEffect(() => {
    if (!startedAt || solved) return;
    const id = setInterval(() => setElapsed(Date.now() - startedAt), 500);
    return () => clearInterval(id);
  }, [startedAt, solved]);

  // Detect solved state — every tile sits at its own correctPos.
  useEffect(() => {
    if (tiles.length === 0 || solved) return;
    if (tiles.every((currentPos, correctPos) => currentPos === correctPos)) {
      setSolved(true);
      const ms = startedAt ? (Date.now() - startedAt) : 0;
      setFinalElapsed(ms);
      try { nativeVibrate([80, 40, 80, 40, 200]); } catch (_) { /* noop */ }
      HarmonySFX.resolve();
      if (!postedRef.current) {
        postedRef.current = true;
        axios.post(`${BACKEND_URL}/api/provenance/progress`, { game: 'fragment', item_id: imageUrl }).catch(() => {});
      }
    }
  }, [tiles, solved, imageUrl, startedAt]);

  // Compute star rating from (moves, finalElapsed). Curve is forgiving but
  // rewards efficient solves: 3★ = ≤40 moves & ≤90s, 2★ = ≤80 moves & ≤180s.
  const starRating = useMemo(() => {
    const secs = finalElapsed / 1000;
    if (moves <= 40 && secs <= 90)  return 3;
    if (moves <= 80 && secs <= 180) return 2;
    return 1;
  }, [moves, finalElapsed]);

  const fmtTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const handleTileClick = (correctPos) => {
    if (solved) return;
    if (selectedId === null) { setSelectedId(correctPos); HarmonySFX.select(); return; }
    if (selectedId === correctPos) { setSelectedId(null); return; }
    setTiles((prev) => {
      const next = prev.slice();
      const a = next[selectedId];
      const b = next[correctPos];
      next[selectedId] = b;
      next[correctPos] = a;
      return next;
    });
    setMoves((m) => m + 1);
    setSelectedId(null);
    HarmonySFX.swap();
  };

  // Re-shuffle the SAME image (fresh scramble, resets stats).
  const reshuffle = () => {
    if (!imageUrl) return;
    setTiles(shuffleUntilUnsolved());
    setSelectedId(null);
    setSolved(false);
    setMoves(0);
    setStartedAt(Date.now());
    setElapsed(0);
    setFinalElapsed(0);
    postedRef.current = false;
    HarmonySFX.shuffle();
  };

  // Advance to the next image in the pool (loops back to 0 at the end while
  // incrementing the internal loop counter — see `cycleCount`).
  const nextImage = () => {
    if (imagePool.length === 0) return;
    setPoolIdx((i) => i + 1);
    HarmonySFX.align();
  };

  return (
    <div className="fixed inset-0 z-[230] flex flex-col imperium-shell"
      style={{ background: 'linear-gradient(180deg, #0a0610 0%, #050308 100%)' }}
      data-testid="provenance-fragment">
      <ImperialBackdrop intensity={0.55}/>
      <div className="relative flex items-center justify-between px-4 py-3 border-b border-[#D4AF37]/15">
        <BackPill onClick={onBack} accent="#D87979" testId="fragment-back"/>
        <span className="font-['Cinzel'] text-[13px] tracking-[0.25em]" style={{ color: GOLD_HI }}>MOSAIC</span>
        <span className="w-6"/>
      </div>

      {!imageUrl ? (
        <div className="relative flex-1 flex items-center justify-center px-6 text-center">
          <p className="font-['Cormorant_Garamond'] italic text-white/50 text-[15px] max-w-sm">
            No image memories yet — upload one to restore your first fragment.
          </p>
        </div>
      ) : (
        <div className="relative flex-1 flex flex-col items-center justify-center px-4 py-4">
          <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
            Tap two tiles to swap
          </p>
          <div className="flex items-center gap-3 mb-1 font-['Outfit'] text-[10px] uppercase tracking-[0.2em] flex-wrap justify-center"
            data-testid="fragment-stats">
            <span style={{ color: GOLD }}>Moves: <span className="text-white">{moves}</span></span>
            <span className="opacity-50" style={{ color: GOLD }}>·</span>
            <span style={{ color: GOLD }}>Time: <span className="text-white">{fmtTime(elapsed)}</span></span>
          </div>

          {/* ─── Ornate gold picture frame around the puzzle grid ─── */}
          <div className="relative mt-2" style={{ width: 'min(96vw, 480px)' }}>
            {/* Outer frame */}
            <div style={{
              position: 'relative',
              padding: 'clamp(10px, 3vw, 16px)',
              borderRadius: 14,
              background:
                `linear-gradient(140deg, rgba(255,233,160,0.10) 0%, rgba(212,175,55,0.04) 50%, rgba(139,107,31,0.08) 100%),
                 linear-gradient(180deg, #120818 0%, #05030a 100%)`,
              border: `1.5px solid ${GOLD}`,
              boxShadow: solved
                ? `0 0 60px ${GOLD}, 0 0 120px ${GOLD}55, inset 0 0 22px ${GOLD}44`
                : `0 0 28px rgba(0,0,0,0.75), 0 0 0 2px ${GOLD}22, inset 0 0 14px rgba(212,175,55,0.08)`,
              transition: 'box-shadow 1200ms ease',
            }}>
              {/* Corner filigree — four gold L-brackets */}
              {[
                { top: 4,    left: 4,    rot: 0   },
                { top: 4,    right: 4,   rot: 90  },
                { bottom: 4, right: 4,   rot: 180 },
                { bottom: 4, left: 4,    rot: 270 },
              ].map((c, i) => (
                <svg key={i} width="22" height="22" viewBox="0 0 22 22"
                  style={{
                    position: 'absolute',
                    ...c,
                    transform: `rotate(${c.rot}deg)`,
                    pointerEvents: 'none',
                    filter: `drop-shadow(0 0 4px ${GOLD}88)`,
                  }} aria-hidden>
                  <path d="M2 8 L2 2 L8 2" fill="none" stroke={GOLD_HI} strokeWidth="1.4" strokeLinecap="round"/>
                  <circle cx="2" cy="2" r="1.6" fill={GOLD}/>
                </svg>
              ))}

              {/* Inner puzzle board (bigger tiles) */}
              <div style={{
                position: 'relative',
                aspectRatio: '1 / 1',
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: 'inset 0 0 0 1px rgba(212,175,55,0.28), inset 0 0 22px rgba(0,0,0,0.55)',
              }}>
                {/* Faint sepia preview underneath the gaps between tiles */}
                <img src={imageUrl} alt="" aria-hidden
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  style={{ filter: 'sepia(1) brightness(0.18)', opacity: 0.4 }}/>

                {tiles.map((currentPos, correctPos) => {
                  const row = Math.floor(currentPos / GRID_SIZE);
                  const col = currentPos % GRID_SIZE;
                  const fragRow = Math.floor(correctPos / GRID_SIZE);
                  const fragCol = correctPos % GRID_SIZE;
                  const isSelected = selectedId === correctPos;
                  const isCorrect = currentPos === correctPos;
                  return (
                    <button key={correctPos}
                      onClick={() => handleTileClick(correctPos)}
                      className="absolute top-0 left-0 p-0 m-0 border-0"
                      style={{
                        width: `${100 / GRID_SIZE}%`,
                        height: `${100 / GRID_SIZE}%`,
                        transform: `translate(${col * 100}%, ${row * 100}%) ${isSelected ? 'scale(0.94)' : 'scale(1)'}`,
                        transition: 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1), filter 1400ms ease, box-shadow 320ms ease',
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: `${GRID_SIZE * 100}% ${GRID_SIZE * 100}%`,
                        backgroundPosition: `${(fragCol / (GRID_SIZE - 1)) * 100}% ${(fragRow / (GRID_SIZE - 1)) * 100}%`,
                        backgroundRepeat: 'no-repeat',
                        filter: solved
                          ? 'sepia(0) brightness(1) saturate(1.08)'
                          : isCorrect
                            ? 'sepia(0.15) brightness(0.92)'
                            : 'sepia(0.85) brightness(0.55)',
                        boxShadow: isSelected
                          ? `inset 0 0 0 2px ${GOLD}, 0 0 14px ${GOLD}aa`
                          : 'inset 0 0 0 1px rgba(0,0,0,0.45)',
                        zIndex: isSelected ? 5 : 1,
                        cursor: solved ? 'default' : 'pointer',
                        willChange: 'transform, filter',
                      }}
                      data-testid={`fragment-tile-${correctPos}`}
                      aria-label={`Tile ${correctPos + 1}${isCorrect ? ' (in place)' : ''}`}
                    />
                  );
                })}

                {/* Restoration flare — single overlay pulse on solve */}
                {solved && (
                  <motion.div
                    className="absolute inset-0 pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 1.8, ease: 'easeOut' }}
                    style={{
                      background: `radial-gradient(circle at center, ${GOLD}55 0%, transparent 70%)`,
                      mixBlendMode: 'screen',
                    }}
                    data-testid="fragment-restoration-flare"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Restored chip + star rating (inline — no separate victory screen) */}
          <AnimatePresence>
            {solved && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-3 flex flex-col items-center gap-1"
                data-testid="fragment-restored-chip">
                <div className="px-4 py-1.5 rounded-full font-['Cinzel'] text-[11px] tracking-[0.28em]"
                  style={{
                    background: `linear-gradient(140deg, ${GOLD_HI} 0%, ${GOLD} 60%, #8B6B1F 100%)`,
                    color: '#0a0610',
                    boxShadow: `0 0 18px ${GOLD}aa, 0 0 36px ${GOLD}55`,
                  }}>
                  ✦ RESTORED · {fmtTime(finalElapsed)} · {moves} moves
                </div>
                <div className="flex items-center gap-2 mt-1" aria-label={`${starRating} of 3 stars`}>
                  {[1, 2, 3].map((i) => {
                    const earned = i <= starRating;
                    return (
                      <motion.svg key={i} width="20" height="20" viewBox="0 0 24 24"
                        initial={{ scale: 0, rotate: -90, opacity: 0 }}
                        animate={{ scale: earned ? 1 : 0.7, rotate: 0, opacity: 1 }}
                        transition={{ delay: 0.15 + i * 0.12, type: 'spring', stiffness: 240, damping: 14 }}>
                        <path d="M12 2 L14.8 8.6 L22 9.3 L16.6 14 L18.3 21 L12 17.3 L5.7 21 L7.4 14 L2 9.3 L9.2 8.6 Z"
                          fill={earned ? GOLD_HI : 'rgba(212,175,55,0.08)'}
                          stroke={earned ? GOLD : 'rgba(212,175,55,0.5)'}
                          strokeWidth="1.2" strokeLinejoin="round"/>
                      </motion.svg>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-3 mt-4">
            <button onClick={reshuffle}
              className="px-4 py-2 rounded-full font-['Outfit'] text-[10px] uppercase tracking-[0.2em]"
              style={{ border: `1px solid ${GOLD}66`, color: GOLD, background: 'rgba(212,175,55,0.06)' }}
              data-testid="fragment-shuffle">
              ↻ Re-shuffle
            </button>
            <button onClick={nextImage}
              disabled={imagePool.length < 2}
              className="px-4 py-2 rounded-full font-['Cinzel'] uppercase tracking-[0.22em] text-[10px]"
              style={{
                background: solved
                  ? `linear-gradient(140deg, ${GOLD_HI} 0%, ${GOLD} 60%, #8B6B1F 100%)`
                  : 'transparent',
                color: solved ? '#0a0610' : GOLD_HI,
                border: `1.5px solid ${GOLD}`,
                boxShadow: solved ? `0 0 18px ${GOLD}aa, 0 0 36px ${GOLD}55` : 'none',
                opacity: imagePool.length < 2 ? 0.4 : 1,
              }}
              data-testid="fragment-next">
              → Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═════════════════════════ Game 4: Echoes of the Empire ═════════════════════════
   A memory-pairing game.  16 (or fewer) cards are dealt face-down, each is a real
   user memory.  Pairs are matched by category tag.  When the board is cleared,
   the 16 photos fuse into a single canvas-rendered Vibe Collage which the user
   can save as a "Legacy" memory.

   Adaptive board sizing — game asks for at least TWO categories with ≥ 2 images.
     • 8 pairs (16 cards) if 8+ eligible categories exist
     • 6 pairs (12 cards) if 6+
     • 4 pairs (8 cards) if 4+
     • 2 pairs (4 cards) if 2+ (minimum playable)
   Zero-cost: all logic & images are local; server pinged once on board clear
   (POST /api/provenance/progress) and once if the user opts to save the collage
   (POST /api/memories).
   ─────────────────────────────────────────────────────────────────────────── */

const ECHO_BACK = '#2a1330';
const ECHO_BACK_HI = '#4a224b';

// Pick exactly 2 random items from an array without mutating it.
const pickTwoRandom = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, 2);
};

const shuffleArr = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const EchoCardBack = ({ size = '100%' }) => (
  // Filigree gold sigil on velvet — used as the card back
  <div className="w-full h-full flex items-center justify-center" style={{
    background: `radial-gradient(circle at 50% 40%, ${ECHO_BACK_HI} 0%, ${ECHO_BACK} 60%, #0a0410 100%)`,
    width: size, height: size,
  }}>
    <svg width="58%" height="58%" viewBox="0 0 40 40">
      <defs>
        <linearGradient id="echoG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GOLD_HI}/>
          <stop offset="100%" stopColor="#8B6B1F"/>
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="14" stroke="url(#echoG)" strokeWidth="0.9" fill="none"/>
      <circle cx="20" cy="20" r="9"  stroke="url(#echoG)" strokeWidth="0.6" fill="none" opacity="0.6"/>
      <path d="M20 8 L23 17 L32 17 L25 22 L28 31 L20 26 L12 31 L15 22 L8 17 L17 17 Z"
        fill="url(#echoG)" opacity="0.92"/>
    </svg>
  </div>
);

/* Memoised so a flip re-renders only the changed card, not the whole board. */
const EchoCard = React.memo(({ card, i, disabled, onPick }) => {
  const showFace = card.flipped || card.matched;
  return (
    <button
      onClick={() => onPick(i)}
      disabled={disabled}
      className="relative rounded-md overflow-hidden p-0 m-0 border-0"
      style={{
        aspectRatio: '1 / 1',
        perspective: 600,
        background: 'transparent',
        boxShadow: card.matched
          ? `0 0 18px ${GOLD}cc, 0 0 36px ${GOLD}66`
          : '0 2px 4px rgba(0,0,0,0.6)',
        transition: 'box-shadow 320ms ease',
        cursor: card.matched ? 'default' : 'pointer',
      }}
      data-testid={`echoes-card-${i}`}
      aria-label={showFace ? `Card ${i + 1} (${card.category})` : `Card ${i + 1} (face down)`}
    >
      <div className="absolute inset-0" style={{
        transformStyle: 'preserve-3d',
        transform: showFace ? 'rotateY(180deg)' : 'rotateY(0deg)',
        transition: 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}>
        {/* Back (face-down) */}
        <div className="absolute inset-0 rounded-md overflow-hidden" style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          border: `1px solid ${GOLD}66`,
        }}>
          <EchoCardBack/>
        </div>
        {/* Front (image / emoji / text) */}
        <div className="absolute inset-0 rounded-md overflow-hidden flex items-center justify-center" style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
          border: card.matched ? `2px solid ${GOLD_HI}` : `1px solid ${GOLD}66`,
          background: card.kind === 'text'
            ? `linear-gradient(160deg, rgba(20,15,10,0.95), rgba(5,3,8,0.98))`
            : card.kind === 'emoji'
              ? `radial-gradient(circle at 40% 40%, rgba(197,160,89,0.14), rgba(5,3,8,0.98))`
              : '#050308',
        }}>
          {card.kind === 'image' ? (
            <>
              <img src={card.thumb} alt=""
                className="w-full h-full object-cover"
                style={{ filter: card.matched ? 'saturate(1.1)' : 'none' }}/>
              <div className="absolute inset-x-0 bottom-0 px-1.5 py-0.5"
                style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)' }}>
                <p className="font-['Outfit'] text-[8px] uppercase tracking-[0.15em] capitalize"
                  style={{ color: card.matched ? GOLD_HI : 'rgba(255,255,255,0.55)' }}>
                  {card.category}
                </p>
              </div>
            </>
          ) : card.kind === 'emoji' ? (
            <EmojiArt emoji={card.emoji} size="min(80px, 19vw)" glow={card.matched}/>
          ) : (
            <span className="font-['Cinzel'] px-1 text-center uppercase tracking-[0.12em]"
              style={{ color: card.matched ? GOLD_HI : '#FFE9A0',
                fontSize: 'min(14px, 3.5vw)', lineHeight: 1.15,
                textShadow: card.matched ? `0 0 8px ${GOLD_HI}` : 'none' }}>
              {card.name}
            </span>
          )}
        </div>
      </div>
    </button>
  );
});

export const EchoesOfEmpire = ({ onBack, memories, blissDeck = null, hostedInModal = false, onChangeCategory = null }) => {
  const [cards, setCards] = useState([]);   // {id,key,imageUrl,category,flipped,matched}
  const [selected, setSelected] = useState([]); // indices (max 2) of currently-flipped unmatched cards
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [resonance, setResonance] = useState(null); // { key, combo }
  const [phase, setPhase] = useState('intro');  // intro | play | collage
  const [collageUrl, setCollageUrl] = useState(null);
  const [collageBlob, setCollageBlob] = useState(null);
  const [savingState, setSavingState] = useState('idle');  // idle | saving | saved | error
  const [moves, setMoves] = useState(0);
  // PREMIUM: combo multiplier (consecutive matches without misses)
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const evaluatingRef = useRef(false);
  const postedRef = useRef(false);

  // Filter to image memories grouped by category
  const groupedByCat = useMemo(() => {
    const g = {};
    for (const m of (memories || [])) {
      if (!m.image_url || m.media_type === 'video' || m.media_type === 'audio') continue;
      if (!g[m.category]) g[m.category] = [];
      g[m.category].push(m);
    }
    return g;
  }, [memories]);

  const eligibleCategories = useMemo(
    () => Object.keys(groupedByCat).filter((c) => groupedByCat[c].length >= 2),
    [groupedByCat]
  );

  // Adaptive board sizing — Echo rule: ≥ 4×4 (8 pairs), grows toward 6×6 (18 pairs).
  // USER mode requires REAL category pairs only (no twin duplicates).
  // BLISS mode uses the built-in deck size.
  const targetPairs = useMemo(() => {
    if (blissDeck) {
      const n = blissDeck.entries.length;
      if (n >= 18) return 18;   // 6×6
      if (n >= 12) return 12;   // 4×6
      return 8;                 // 4×4
    }
    let capacity = 0;
    for (const c of eligibleCategories) {
      capacity += Math.floor(groupedByCat[c].length / 2);
    }
    if (capacity >= 18) return 18;   // 6×6
    if (capacity >= 12) return 12;   // 4×6
    if (capacity >= 8)  return 8;    // 4×4
    return capacity;                 // still capped by real category pairs
  }, [eligibleCategories, groupedByCat, blissDeck]);

  const canPlay = blissDeck
    ? true
    : (targetPairs >= 8 && eligibleCategories.length >= 8);

  const dealCards = () => {
    if (!canPlay) return;
    if (blissDeck) EchoSFX.deal();
    const dealt = [];

    if (blissDeck) {
      // BLISS mode — pic-to-pic: each entry generates TWO identical emoji cards.
      const picks = shuffleArr(blissDeck.entries).slice(0, targetPairs);
      picks.forEach((e) => {
        for (let k = 0; k < 2; k++) {
          dealt.push({
            key: `bliss_${e.name}_${k}_${Math.random().toString(36).slice(2, 6)}`,
            kind: 'emoji', name: e.name, emoji: e.emoji,
            category: e.name,       // match key — same for both cards in the pair
            flipped: false, matched: false,
          });
        }
      });
    } else {
      // USER mode — real category pairs only from user memories.
      const catQueue = shuffleArr(eligibleCategories);
      for (const cat of catQueue) {
        const pool = shuffleArr(groupedByCat[cat]);
        while (pool.length >= 2 && dealt.length / 2 < targetPairs) {
          const a = pool.shift(); const b = pool.shift();
          [a, b].forEach((m, i) => {
            dealt.push({
              key: `${m.id}_${i}_${Math.random().toString(36).slice(2, 6)}`,
              kind: 'image', imageUrl: m.image_url,
              thumb: m.thumb_url || m.image_url,
              category: cat,
              flipped: false, matched: false,
            });
          });
        }
        if (dealt.length / 2 >= targetPairs) break;
      }
    }

    setCards(shuffleArr(dealt));
    setSelected([]);
    setMatchedPairs(0);
    setMoves(0);
    setCombo(0);
    setMaxCombo(0);
    setCollageUrl(null);
    setCollageBlob(null);
    setSavingState('idle');
    postedRef.current = false;
    evaluatingRef.current = false;
    setPhase('play');
  };

  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const comboRef = useRef(combo);
  comboRef.current = combo;
  // Sound + haptic fire on the SAME tap that reveals the card — a match
  // chimes the instant the 2nd card turns, never after a delay.
  const handleCardClick = useCallback((idx) => {
    if (evaluatingRef.current) return;
    const all = cardsRef.current;
    const c = all[idx];
    if (!c || c.flipped || c.matched) return;
    const sel = selectedRef.current;
    if (sel.length >= 2 || sel.includes(idx)) return;
    const nextSel = [...sel, idx];
    selectedRef.current = nextSel;
    if (nextSel.length === 2 && all[nextSel[0]].category === c.category) {
      EchoSFX.flip();
      EchoSFX.match(comboRef.current + 1);
      nativeVibrate([40, 30, 60]);
    } else {
      EchoSFX.flip();
    }
    setCards((list) => list.map((card, i) => i === idx ? { ...card, flipped: true } : card));
    setSelected(nextSel);
  }, []);

  // Evaluate when two cards are selected
  useEffect(() => {
    if (selected.length !== 2 || evaluatingRef.current) return;
    evaluatingRef.current = true;
    const [a, b] = selected;
    const sameCat = cards[a].category === cards[b].category;
    setMoves((m) => m + 1);
    const delay = sameCat ? 280 : 900;
    const t = setTimeout(() => {
      setCards((all) => all.map((card, i) => {
        if (i === a || i === b) {
          if (sameCat) return { ...card, matched: true, flipped: true };
          return { ...card, flipped: false };
        }
        return card;
      }));
      if (sameCat) {
        setMatchedPairs((p) => p + 1);
        // PREMIUM: combo chain — consecutive matches without a miss
        setCombo((c) => {
          const next = c + 1;
          setMaxCombo((m) => Math.max(m, next));
          return next;
        });
        setResonance({ key: Date.now(), combo: combo + 1 });
        setTimeout(() => setResonance(null), 1500);
      } else {
        setCombo(0);  // miss breaks the chain
        try { nativeVibrate(60); } catch (_) {}
      }
      setSelected([]);
      evaluatingRef.current = false;
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // When all pairs matched → generate collage
  useEffect(() => {
    if (cards.length === 0) return;
    if (matchedPairs * 2 !== cards.length) return;
    if (postedRef.current) return;
    postedRef.current = true;
    // Log progress immediately (zero-cost)
    axios.post(`${BACKEND_URL}/api/provenance/progress`,
      { game: 'echoes', item_id: `echoes_${Date.now()}` }).catch(() => {});
    // Bliss mode: skip collage generation — instant "Completed Successfully" overlay.
    if (blissDeck) {
      EchoSFX.victory();
      try { nativeVibrate([100, 50, 100, 50, 200]); } catch (_) { /* noop */ }
      setPhase('collage');
      return;
    }
    // User mode: build collage (async — sets state when blob is ready)
    buildCollage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedPairs, cards.length]);

  const buildCollage = async () => {
    try {
      const pairs = cards.length / 2;
      const cols = pairs >= 8 ? 4 : pairs >= 4 ? 4 : pairs >= 2 ? 2 : 2;
      const rows = Math.ceil(cards.length / cols);
      const TILE = 220;
      const PAD = 8;
      const TITLE_H = 64;
      const W = cols * TILE + (cols + 1) * PAD;
      const H = rows * TILE + (rows + 1) * PAD + TITLE_H;

      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      // velvet background
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, '#1a0f24'); grad.addColorStop(1, '#050308');
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);

      const loadImage = (url) => new Promise((res) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => res(img);
        img.onerror = () => res(null);
        img.src = url;
      });

      for (let i = 0; i < cards.length; i++) {
        const c = cards[i];
        const r = Math.floor(i / cols);
        const col = i % cols;
        const x = PAD + col * (TILE + PAD);
        const y = PAD + r * (TILE + PAD);
        const img = await loadImage(c.imageUrl);
        if (img) {
          const ratio = Math.max(TILE / img.width, TILE / img.height);
          const sw = TILE / ratio, sh = TILE / ratio;
          const sx = (img.width - sw) / 2;
          const sy = (img.height - sh) / 2;
          ctx.drawImage(img, sx, sy, sw, sh, x, y, TILE, TILE);
        } else {
          ctx.fillStyle = '#251830'; ctx.fillRect(x, y, TILE, TILE);
        }
        // Gold border + inner highlight
        ctx.strokeStyle = '#D4AF37'; ctx.lineWidth = 2.5;
        ctx.strokeRect(x + 1, y + 1, TILE - 2, TILE - 2);
        ctx.strokeStyle = 'rgba(255,233,160,0.35)'; ctx.lineWidth = 1;
        ctx.strokeRect(x + 4, y + 4, TILE - 8, TILE - 8);
      }

      // Title strip
      ctx.fillStyle = '#FFE9A0';
      ctx.textAlign = 'center';
      ctx.font = '700 26px "Cinzel", serif';
      ctx.fillText('ECHOES OF THE EMPIRE', W / 2, H - 32);
      ctx.fillStyle = 'rgba(212,175,55,0.7)';
      ctx.font = 'italic 14px "Cormorant Garamond", serif';
      ctx.fillText(
        `Resonance found · ${new Date().toLocaleDateString()} · ${moves} moves`,
        W / 2, H - 10
      );

      await new Promise((res) => {
        canvas.toBlob((blob) => {
          if (blob) {
            setCollageBlob(blob);
            setCollageUrl(URL.createObjectURL(blob));
          }
          res();
        }, 'image/jpeg', 0.88);
      });
      try { nativeVibrate([100, 50, 100, 50, 200]); } catch (_) {}
      setPhase('collage');
    } catch (_) {
      // fail silent — user still sees matched board
      setPhase('collage');
    }
  };

  const saveCollageAsMemory = async () => {
    if (!collageBlob || savingState === 'saving') return;
    setSavingState('saving');
    try {
      const fd = new FormData();
      fd.append('category', 'legacy');
      fd.append('note', `Echoes Mastery · ${moves} moves`);
      fd.append('media_type', 'image');
      fd.append('file', collageBlob, `echoes_${Date.now()}.jpg`);
      await axios.post(`${BACKEND_URL}/api/memories`, fd);
      setSavingState('saved');
    } catch (_) {
      setSavingState('error');
    }
  };

  // Responsive columns: 6×6 at 36 cards, 4×N otherwise.
  const boardCols = cards.length >= 36 ? 6 : 4;
  const embedded = hostedInModal || !!blissDeck;

  return (
    <div className={embedded ? "flex-1 flex flex-col min-h-0 relative"
                              : "fixed inset-0 z-[230] flex flex-col imperium-shell"}
      style={{
        background: 'radial-gradient(ellipse at top, #1a0f24 0%, #050308 75%)',
      }}
      data-testid="echoes-empire">
      <ImperialBackdrop intensity={0.6}/>
      {!embedded && (
        <div className="relative flex items-center justify-between px-4 py-3 border-b border-[#D4AF37]/15">
          <BackPill onClick={onBack} accent="#D87979" testId="echoes-back"/>
          <span className="font-['Cinzel'] text-[13px] tracking-[0.25em]" style={{ color: GOLD_HI }}>
            ECHOES OF THE EMPIRE
          </span>
          <span className="w-6"/>
        </div>
      )}

      {phase === 'intro' && (
        <div className="relative flex-1 flex flex-col items-center justify-center px-6 py-6 text-center">
          <p className="font-['Cormorant_Garamond'] italic text-white/65 text-[16px] max-w-md mb-2">
            Pair each Anchor with its Echo — memories that share the same vibe.
          </p>
          <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-white/35 mb-6">
            Tap two cards · same category fuses
          </p>
          {canPlay ? (
            <>
              <div className="px-5 py-3 rounded-xl mb-5"
                style={{ background: 'rgba(212,175,55,0.06)', border: `1px solid ${GOLD}33` }}>
                <p className="font-['Cinzel'] text-[14px] tracking-[0.2em]" style={{ color: GOLD_HI }}>
                  {targetPairs} PAIRS · {targetPairs * 2} CARDS
                </p>
                <p className="font-['Cormorant_Garamond'] italic text-white/55 text-[13px] mt-1">
                  Drawn from {targetPairs} of your tagged categories
                </p>
              </div>
              <button onClick={dealCards}
                className="px-6 py-3 rounded-full font-['Cinzel'] uppercase tracking-[0.3em] text-[12px]"
                style={{ background: GOLD, color: '#0a0610', boxShadow: `0 0 24px ${GOLD}66` }}
                data-testid="echoes-start">
                Deal the Board
              </button>
            </>
          ) : (
            <div className="max-w-sm">
              <p className="font-['Cinzel'] text-[14px] tracking-[0.2em] mb-3"
                style={{ color: '#DC143C' }} data-testid="echoes-locked">
                BOARD LOCKED
              </p>
              <p className="font-['Cormorant_Garamond'] italic text-white/60 text-[14px]">
                Echoes requires at least <span style={{ color: GOLD }}>16 uploads</span> spread across
                <span style={{ color: GOLD }}> 8+ categories</span> (2 memories per category minimum).
              </p>
              <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.25em] text-white/35 mt-4">
                Upload more memories, or try a Bliss deck instead
              </p>
            </div>
          )}
        </div>
      )}

      {phase === 'play' && (
        <div className="relative flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 flex items-center justify-center gap-4 pt-3 pb-2 font-['Outfit'] text-[10px] uppercase tracking-[0.25em]">
            <span className="text-white/40">Pairs: <span style={{ color: GOLD }}>{matchedPairs}/{cards.length / 2}</span></span>
            <span className="text-white/40">Moves: <span style={{ color: GOLD }}>{moves}</span></span>
            {combo >= 2 && (
              <motion.span key={combo}
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                className="text-white/40"
                data-testid="echoes-combo">
                Combo: <span style={{ color: GOLD_HI, textShadow: `0 0 8px ${GOLD}` }}>×{combo}</span>
              </motion.span>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center px-3 pb-3">
          <div className="relative grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${boardCols}, minmax(0, 1fr))`,
              width: 'min(92vw, 420px)',
            }}
            data-testid="echoes-grid">
            {cards.map((card, i) => {
              return (
                <EchoCard key={card.key} card={card} i={i}
                  disabled={card.matched || (selected.length >= 2 && !card.flipped)}
                  onPick={handleCardClick}/>
              );
            })}

            {/* Resonance flash overlay */}
            <AnimatePresence>
              {resonance && (
                <motion.div key={resonance.key}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  data-testid="echoes-resonance-toast">
                  <div className="px-5 py-2 rounded-full"
                    style={{
                      background: 'linear-gradient(140deg, #1a1024, #0a0612)',
                      border: `1.5px solid ${GOLD}`,
                      boxShadow: `0 0 30px ${GOLD}cc, 0 0 60px ${GOLD}66`,
                    }}>
                    <span className="font-['Cinzel'] text-[12px] tracking-[0.3em]" style={{ color: GOLD_HI }}>
                      ✦ RESONANCE FOUND
                      {resonance.combo >= 3 && (
                        <span className="ml-2" style={{ color: '#FFFFFF', textShadow: `0 0 6px ${GOLD_HI}` }}>
                          ×{resonance.combo}
                        </span>
                      )}
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          </div>

          {/* Footer — Reshuffle + Next Round always visible during play.
              paddingBottom uses safe-area-inset-bottom (Android edge-to-edge WebView)
              so nothing is hidden behind the phone's system nav bar. */}
          {blissDeck && (
            <div className="flex-shrink-0 flex items-center justify-center gap-3 pt-2 px-3 border-t"
              style={{
                borderColor: `${GOLD}22`,
                background: 'rgba(5,3,8,0.85)',
                paddingBottom: 'max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 20px)',
              }}
              data-testid="echoes-play-footer">
              <button onClick={dealCards}
                data-testid="echoes-play-reshuffle"
                className="flex-1 max-w-[160px] px-4 py-2.5 rounded-full font-['Outfit'] uppercase tracking-[0.24em]"
                style={{ border: `1px solid ${GOLD}66`, color: GOLD, fontSize: 10 }}>
                ↻ Reshuffle
              </button>
              {onChangeCategory && (
                <button onClick={onChangeCategory}
                  data-testid="echoes-play-next"
                  className="flex-1 max-w-[160px] px-4 py-2.5 rounded-full font-['Cinzel'] uppercase tracking-[0.24em]"
                  style={{ background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`, color: '#0a0610',
                    fontSize: 10, boxShadow: `0 0 14px ${GOLD}55` }}>
                  → Next Round
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {phase === 'collage' && blissDeck && (
        <div className="absolute inset-0 z-[10] flex items-center justify-center px-6"
          data-testid="echoes-bliss-result"
          style={{ background: 'radial-gradient(ellipse at center, rgba(5,3,8,0.85) 0%, rgba(5,3,8,0.98) 70%)' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18 }}
            className="rounded-2xl w-full max-w-sm px-6 py-7 text-center"
            style={{
              background: 'linear-gradient(160deg, #17101f 0%, #050308 100%)',
              border: `1.5px solid ${GOLD}`,
              boxShadow: `0 0 40px ${GOLD}66, inset 0 0 26px ${GOLD}18`,
            }}>
            <p className="font-['Outfit'] uppercase tracking-[0.32em] mb-3"
              style={{ color: GOLD, fontSize: 10 }}>
              {blissDeck.label} · Cycle {blissDeck.cycleLabel || ''}
            </p>
            <p className="font-['Cinzel'] tracking-[0.24em] mb-2"
              style={{ color: '#FFE9A0', fontSize: 22, textShadow: `0 0 16px ${GOLD}88` }}>
              YOU WIN
            </p>
            <p className="font-['Cormorant_Garamond'] italic mb-5"
              style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15 }}>
              Successfully matched all pairs.
            </p>
            <div className="flex items-center justify-center gap-4 font-['Outfit'] text-[10px] uppercase tracking-[0.25em] mb-3"
              style={{ color: 'rgba(255,255,255,0.55)' }}>
              <span>Pairs · <span style={{ color: GOLD_HI }}>{cards.length / 2}</span></span>
              <span>Moves · <span style={{ color: GOLD_HI }}>{moves}</span></span>
              {maxCombo >= 2 && <span>Combo · <span style={{ color: GOLD_HI }}>×{maxCombo}</span></span>}
            </div>
            <div className="flex flex-col gap-2.5">
              {onChangeCategory && (
                <button onClick={onChangeCategory}
                  data-testid="echoes-next-round"
                  className="w-full px-5 py-3 rounded-full font-['Cinzel'] uppercase tracking-[0.28em]"
                  style={{ background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`,
                    color: '#0a0610', fontSize: 12,
                    boxShadow: `0 0 22px ${GOLD}77` }}>
                  → Next Round
                </button>
              )}
              <button onClick={dealCards}
                data-testid="echoes-replay"
                className="w-full px-5 py-2.5 rounded-full font-['Outfit'] uppercase tracking-[0.25em]"
                style={{ border: `1px solid ${GOLD}66`, color: GOLD, fontSize: 11 }}>
                ↻ Reshuffle
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {phase === 'collage' && !blissDeck && (
        <div className="relative flex-1 flex flex-col items-center justify-center overflow-y-auto px-4 py-6">
          <p className="font-['Cinzel'] text-[16px] tracking-[0.3em] mb-2" style={{ color: GOLD_HI }}>
            VIBE COLLAGE
          </p>
          <p className="font-['Cormorant_Garamond'] italic text-white/55 text-[13px] mb-3">
            All resonances found in <span style={{ color: GOLD }}>{moves}</span> moves
            {maxCombo >= 2 && <> · Max combo <span style={{ color: GOLD_HI }}>×{maxCombo}</span></>}
          </p>

          {/* PREMIUM: 3-star efficiency rating for Echoes.
              Perfect = moves equals pairs (no misses). 3★ ≤ pairs*1.5, 2★ ≤ pairs*2.2 */}
          {(() => {
            const pairs = cards.length / 2;
            const stars = moves <= pairs * 1.5 ? 3 : moves <= pairs * 2.2 ? 2 : 1;
            return (
              <div className="flex items-center gap-2.5 mb-4" data-testid="echoes-star-rating" aria-label={`${stars} of 3 stars`}>
                {[1, 2, 3].map((i) => {
                  const earned = i <= stars;
                  return (
                    <motion.svg key={i} width="26" height="26" viewBox="0 0 24 24"
                      initial={{ scale: 0, rotate: -90, opacity: 0 }}
                      animate={{ scale: earned ? 1 : 0.7, rotate: 0, opacity: 1 }}
                      transition={{ delay: 0.4 + i * 0.18, type: 'spring', stiffness: 220, damping: 14 }}>
                      <path d="M12 2 L14.8 8.6 L22 9.3 L16.6 14 L18.3 21 L12 17.3 L5.7 21 L7.4 14 L2 9.3 L9.2 8.6 Z"
                        fill={earned ? GOLD_HI : 'rgba(212,175,55,0.08)'}
                        stroke={earned ? GOLD : 'rgba(212,175,55,0.5)'}
                        strokeWidth="1.2" strokeLinejoin="round"/>
                    </motion.svg>
                  );
                })}
              </div>
            );
          })()}

          {collageUrl ? (
            <motion.img src={collageUrl} alt="Vibe Collage"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-md"
              style={{ width: 'min(85vw, 360px)', boxShadow: `0 0 60px ${GOLD}aa, 0 0 120px ${GOLD}55` }}
              data-testid="echoes-collage-image"
            />
          ) : (
            <div style={{ width: 'min(85vw, 360px)', aspectRatio: '1 / 1' }}
              className="flex items-center justify-center rounded-md"
              data-testid="echoes-collage-loading">
              <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-white/40">
                Fusing collage…
              </p>
            </div>
          )}

          <div className="flex gap-3 mt-6 flex-wrap justify-center">
            <button onClick={saveCollageAsMemory}
              disabled={!collageBlob || savingState === 'saving' || savingState === 'saved'}
              className="px-5 py-2.5 rounded-full font-['Cinzel'] uppercase tracking-[0.25em] text-[11px]"
              style={{
                background: savingState === 'saved' ? 'rgba(212,175,55,0.2)' : GOLD,
                color: savingState === 'saved' ? GOLD_HI : '#0a0610',
                opacity: !collageBlob ? 0.4 : 1,
                cursor: !collageBlob ? 'default' : 'pointer',
              }}
              data-testid="echoes-save-collage">
              {savingState === 'saving' ? 'Saving…'
                : savingState === 'saved' ? '✦ Saved to Legacy'
                : savingState === 'error' ? 'Retry Save'
                : 'Save to Profile'}
            </button>
            <button onClick={dealCards}
              className="px-5 py-2.5 rounded-full font-['Outfit'] uppercase tracking-[0.25em] text-[11px]"
              style={{ border: `1px solid ${GOLD}66`, color: GOLD }}
              data-testid="echoes-replay">
              ↻ Reshuffle
            </button>
            {onChangeCategory && (
              <button onClick={onChangeCategory}
                className="px-5 py-2.5 rounded-full font-['Outfit'] uppercase tracking-[0.25em] text-[11px]"
                style={{ background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`, color: '#0a0610',
                  boxShadow: `0 0 14px ${GOLD}55` }}
                data-testid="echoes-change-category">
                → Change
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


/* ───────────────────────── Provenance Launcher ─────────────────────────
   Only the Provenance Fragment rite ships in this build. The other three
   games (Constellation Harmony, Legacy Duel, Empire's Harmony) are archived
   in `_backups/Provenance.full.jsx.bak` for future re-introduction. */

export const Provenance = ({ onClose, onMaster }) => {
  const [memories, setMemories] = useState([]);
  const prevMasterRef = useRef(false);

  useEffect(() => {
    // Load user memories once — Fragment picks its source image from these.
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/memories`)
      .then(({ data }) => { if (!cancelled) setMemories(Array.isArray(data) ? data : []); })
      .catch(() => {});
    // Fire onMaster the first time the backend flips this flag (kept for parity
    // with the multi-game launcher — a Fragment solve alone can also trigger it).
    axios.get(`${BACKEND_URL}/api/provenance/progress`)
      .then(({ data }) => {
        if (data?.is_master && !prevMasterRef.current) {
          onMaster && onMaster();
          prevMasterRef.current = true;
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [onMaster]);

  return <ProvenanceFragment onBack={onClose} memories={memories} />;
};

export default Provenance;
